import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import gradient from './gradient';
import imageurl from './image';
import solid from './solid';

export default Vue.component('background-options', {
  template: require('./template.html'),
  name: 'background-options',
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    schemeColors() {
      return [{ color: '', type: 'picker' }];
    },
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
    hasAreas() {
      return Object.keys(this.areas).length > 0;
    },
    areaChecked() {
      return this.hasAreas && !this.areas.all && Object.values(this.areas).includes(true);
    },
  },
  components: {
    gradient,
    imageurl,
    solid,
  },
  props: ['option', 'active-state', 'dom-nodes', 'component-config'],
  data() {
    const imageFile = `${process.env.S3_ASSETS_URL}mock-images/backgrounds/image-bg.svg`;
    return {
      debTime: 250,
      innerDomNodes: {},
      areas: {},
      defaultValues: {
        solid: 'rgba(255, 255, 255, 1)',
        gradient: 'linear-gradient(90deg, rgba(65, 224, 196, 1) 0%, rgba(141, 138, 238, 1) 100%)',
        image: `center center / cover no-repeat url(${imageFile})`,
        imageFile,
      },
      cached: {
        solid: {
          mobile: null,
          tablet: null,
          desktop: null,
        },
        gradient: {
          mobile: null,
          tablet: null,
          desktop: null,
        },
        image: {
          mobile: null,
          tablet: null,
          desktop: null,
        },
      },
      bgType: false,
      initialRule: null,
      initialValue: '',
    };
  },
  watch: {
    viewport() {
      this.getBgType();
    },
    activeState() {
      this.getBgType();
    },
    activeDropletItem: {
      handler() {
        this.getBgType();
      },
      deep: true,
    },
  },
  created() {
    if (this.option.payload.areas) {
      _.forOwn(this.option.payload.areas, (area) => {
        this.$set(this.areas, area.property, area.property === 'all');
      });
    }
    this.getBgType(true);
  },
  methods: {
    updateStyles(params) {
      return new Promise((resolve) => {
        const bgType = params.bgType || this.bgType;
        this._debounce(() => {
          const payload = {};
          payload.drp = this.activeDroplet;
          if (params.defaultValue) {
            // get default values if being set for the first time
            payload.val = this.cached[bgType][this.viewport] || this.defaultValues[bgType];
          } else if (bgType === 'solid') {
            payload.val = params.solidVal;
          } else if (bgType === 'gradient') {
            payload.val = params.gradientVal;
          } else if (bgType === 'image') {
            payload.val = params.imageVal;
          }
          payload.viewport = this.viewport;
          let updates = _.cloneDeep(params.updates);
          // if there is at least one area checked besides all
          if (this.hasAreas && this.areaChecked) {
            const checked = _.keys(_.pickBy(this.areas));
            updates = _.filter(updates, update => checked.includes(update.property));
          }
          _.forEach(updates, (item) => {
            let selector;
            if (this.activeDropletItem._id && this.componentConfig.itemList) {
              // this is in the event a user is targeting a specific item within a droplet
              // eg, a nav link or a column in columns
              const inner = this.componentConfig.itemList.selectors[item.property];
              const nested = inner || '';
              selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
            } else {
              this.resetCustomQueries(item);
              selector = item.selector.replace(/\$\{state\}/gi, this.activeState);
              selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
            }
            payload.property = item.property;
            payload.selector = selector;
            payload.noId = item.noId;
            if (item.valueHelper) {
              payload.val = this[item.valueHelper](payload.val);
            }
            this.$emit('check-legacy-styles', payload);
            this.$store.dispatch('changeDropletCss', payload);
          });
          this.$emit('css-generator', this.cssPayload(), this.option.plugin);
          this.$emit('update-changes', true);
          // update cached values
          this.cached[bgType][this.viewport] = payload.val;
          resolve();
        }, this.debTime);
      });
    },
    resetCustomQueries(update) {
      if (!this.componentConfig.itemList) {
        return;
      }
      const inner = this.componentConfig.itemList.selectors[update.property];
      const nested = inner || '';
      const customRule = new RegExp(`#drp-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}${nested}`, 'gi');
      _.forEach(this.activeDroplet.mediaQueries, (rule) => {
        if (rule.selector.match(customRule)) {
          const property = _.find(rule.properties, r => r.property === update.property);
          if (property) {
            this.$store.dispatch('setDropletVal', {
              drp: property,
              key: this.viewport,
              val: 'default',
            });
          }
        }
      });
    },
    async getBgType(cache) {
      let type = 'image';
      const option = _.cloneDeep(this.option);
      const first = option.payload.updates[0];
      let rule = first;
      let groupParent;
      if (this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        rule = _.find(option.payload.updates, update => checked.includes(update.property));
      }
      let selector;
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        groupParent = first;
        const inner = this.componentConfig.itemList.selectors[rule.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
      } else {
        selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
      }
      const property = rule.property;
      // if one area is checked, get that rule from the updates list
      const useRoot = option.$el === 'root';
      const $root = this.domNodes.$dropletRow;
      const $el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];

      if (this.typeAllowed('image')) {
        // if there is a legacy bg image, we need to set to default and
        // generate rule for new background css rule
        const legacyImg = this._getBgImg(this, selector, 'background-image', this.activeDroplet);
        if (legacyImg.match(/url\(/gi)) {
          await this.resetLegacyBgImg({ legacyImg, $el, selector });
        }
      }

      const value = this._getPropertyRule({
        $vm: this,
        default: this.defaultValues.solid,
        $el,
        selector,
        property,
        groupParent,
      });

      if (value.match(/^rgba\(|^rgb\(/gi)) {
        type = 'solid';
      } else if (value.match(/^linear-gradient\(|^radial-gradient\(/gi)) {
        type = 'gradient';
      }
      this.initialRule = value;
      this.bgType = type;

      if (cache) {
        this.setCache();
      }
    },
    async resetLegacyBgImg(params) {
      const updates = this.option.payload.updates;
      // size
      const defaultSize = 'cover';
      let size = this._getPropertyRule({
        $vm: this,
        default: defaultSize,
        $el: params.$el,
        selector: params.selector,
        property: 'background-size',
      });
      size = size === 'initial' ? defaultSize : size;
      // position
      const defaultPosition = 'center center';
      let position = this._getPropertyRule({
        $vm: this,
        default: defaultPosition,
        $el: params.$el,
        selector: params.selector,
        property: 'background-position',
      });
      position = position === 'initial' ? defaultPosition : position;
      // repeat
      const defaultRepeat = 'no-repeat';
      let repeat = this._getPropertyRule({
        $vm: this,
        default: defaultRepeat,
        $el: params.$el,
        selector: params.selector,
        property: 'background-repeat',
      });
      repeat = repeat === 'initial' ? defaultRepeat : repeat;

      // now set legacy to default so it won't get picked up again
      await this.updateStyles({
        ...this.option.payload,
        bgType: 'image',
        imageVal: 'default',
        updates: updates.map(u => ({ ...u, property: 'background-image' })),
      });

      // build new string and set mediaQueries to use this new value
      const newString = `${position} / ${size} ${repeat} ${params.legacyImg}`;
      await this.updateStyles({
        ...this.option.payload,
        bgType: 'image',
        imageVal: newString,
      });

      return newString;
    },
    setCache() {
      this.cached[this.bgType][this.viewport] = this.initialRule;
    },
    checkArea(area) {
      _.forOwn(this.areas, (value, key) => {
        if (area !== key) {
          this.areas[key] = false;
        }
      });
      if ((area === 'all' && !this.areas.all) || (area !== 'all' && !this.areaChecked)) {
        this.areas.all = true;
      }
      this.getBgType();
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    async changeBgType(type) {
      await this.updateStyles({
        ...this.option.payload,
        bgType: type,
        defaultValue: true,
      });
      this.bgType = type;
    },
    typeAllowed(type) {
      return !this.option.input.hiddenTypes ||
      (this.option.input.hiddenTypes && !this.option.input.hiddenTypes.includes(type));
    },
    startCase(string) {
      return _.startCase(string);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/background-options/index.js